import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useScreenSize } from "../../components/auxiliaryFunctions";
import styles from "./Apply.module.css";

import CommentInput from "../../components/ui/CommentInput";
import Notification from "../../components/notification/Notification";
import SkeletonApply from "../../components/skeleton/apply/SkeletonApply";
import { ModalInfoCompleted } from "../../components/modalInfo/ModalInfo";
import DownloadTheApplicationFinish from "../../components/downloadTheApplicationFinish/DownloadTheApplicationFinish";
import ErrorModal from "../../components/errorModalInfo/ErrorModal";

import { useApi } from '../../utils/useApi';
import { createAuthApi } from './api';

const Apply = () => {
    const navigate = useNavigate();
    const { customFetch } = useApi();
    const { isMobile } = useScreenSize();
    const authApi = createAuthApi(customFetch);
    const [loading, setLoading] = useState(true);

    const location = useLocation();
    const event = location.state;
    const [questions, setQuestions] = useState([]);
    const [errors, setErrors] = useState({});
    const [isAgreed, setIsAgreed] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [showNotification, setShowNotification] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isModalErrorOpen, setIsModalErrorOpen] = useState(false);
    const [errorText, setErrorText] = useState("");

    const handleInputChange = (field, value) => {
        setQuestions(prev =>
            prev.map(item =>
                item.id === field
                    ? { ...item, answer: value, error: "" }
                    : item
            )
        );
    };

    const clickModal = () => {
        setIsModalOpen(false);
        navigate("/");
    }

    const handleAgreementChange = (e) => {
        setIsAgreed(e.target.checked);
        if (errors.agreement) {
            setErrors(prev => ({ ...prev, agreement: "" }));
        }
    };

    const validateForm = () => {
        let isValid = true;
        const newErrors = {};

        // Проверяем вопросы
        questions.forEach(item => {
            if (!item.answer || !item.answer.trim()) {
                newErrors[item.id] = "Пожалуйста, заполните данное поле";
                isValid = false;
            }
        });

        // Проверяем согласие
        if (!isAgreed) {
            newErrors.agreement = "Необходимо подтвердить свое участие";
            isValid = false;
        }

        // Обновляем ошибки в вопросах
        setQuestions(prev =>
            prev.map(item => ({
                ...item,
                error: newErrors[item.id] || ""
            }))
        );

        setErrors(newErrors);
        return isValid;
    };

    const sendAnApplication = async () => {
        setIsSaving(true);
        try {
            const answers = questions.map((question) => ({
                questionId: question.id,
                userAnswer: question.answer,
            }));

            const result = await authApi.postSubmitApplication(event.id, answers);
            if (result.applicationSubmitted) {
                setIsModalOpen(true);
            } else if (!result.applicationSubmitted) {
                setIsModalErrorOpen(true);
                setErrorText(result.reason);
            }
        } catch (error) {
            console.error('postSubmitApplication failed:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitted(true);

        if (validateForm()) {
            await sendAnApplication();
        } else {
            setShowNotification(true);
        }
    };

    const getQuestions = async () => {
        setLoading(true);
        try {
            const result = await authApi.getApplicationsQuestions(event.id);
            if (result) {
                const newQuestions = result.questions.map((question) => ({
                    id: question.questionId,
                    title: question.questionTitle,
                    clue: question.question,
                    answer: "",
                    isSeveralAnswers: question.isSeveralAnswers,
                    answersIsLink: question.answersIsLink,
                    error: "",
                }));
                setQuestions(newQuestions);
            }
        } catch (error) {
            console.error('Events failed:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (event) {
            getQuestions();
        }
    }, [event]);

    return (
        <>
            <main className={styles.mainContent}>
                {loading ? (
                    <SkeletonApply />
                ) : (
                    <>
                        {!isMobile && (
                            <div className={styles.headerBox}>
                                <h1 className={styles.title}>Заявка на мероприятие</h1>
                            </div>
                        )
                        }

                        <form onSubmit={handleSubmit} className={styles.bodyContainer}>
                            {questions.map((item) => (
                                <CommentInput
                                    key={item.id}
                                    label={item.title}
                                    clue={item.clue}
                                    title={"Ваш ответ"}
                                    value={item.answer}
                                    onChange={(e) => handleInputChange(item.id, e.target.value)}
                                    error={item.error}
                                    display={item.answersIsLink ? "input" : (item.clue === "" ? "1" : "textarea")}
                                />
                            ))}
                            <div className={styles.basement}>
                                <div className={styles.ckeck}>
                                    <label className={styles.checkboxLabel}>
                                        <input
                                            type="checkbox"
                                            className={styles.checkboxInput}
                                            checked={isAgreed}
                                            onChange={handleAgreementChange}
                                        />
                                        <span className={styles.checkboxCustom}></span>
                                        <p className={styles.textCheck}>
                                            Я подтверждаю своё участие во всех днях секции ВФТМ в рамках Балтийского культурного форума в Светлогорске.
                                        </p>
                                    </label>
                                    {isSubmitted && errors.agreement && (
                                        <p className={styles.errorText}>{errors.agreement}</p>
                                    )}
                                </div>
                                <div className={styles.button}>
                                    <button
                                        type="submit"
                                        disabled={isSaving}
                                        className={styles.submitButton}
                                    >
                                        {isSaving ? (
                                            <div className={styles.savingContent}>
                                                <span>ОТПРАВЛЯЕМ</span>
                                                <div className={styles.loadingSpinner}></div>
                                            </div>
                                        ) : (
                                            "ОТПРАВИТЬ"
                                        )}
                                    </button>
                                </div>
                                <Notification
                                    message="Пожалуйста, заполните все обязательные поля"
                                    visible={showNotification}
                                    onClose={() => setShowNotification(false)}
                                />
                            </div>
                        </form>
                    </>
                )}
            </main>
            {isModalOpen && (
                <DownloadTheApplicationFinish
                    isModalOpen={isModalOpen}
                    onClose={clickModal}
                />
            )}
            {isModalErrorOpen && (
                <ErrorModal
                    errorText={errorText}
                    onClose={clickModal}
                />
            )}

        </>
    );
};

export default Apply;